import { MapperHelper } from '@helpers';
import clsx from 'clsx';
import React from 'react';

import styles from './ScoreBar.module.scss';

export interface ScoreInterval {
  lo: number;
  hi: number;
  label: string;
}

interface ScoreBarProps {
  value: number;
  maxValue?: number;
  limits?: number[];
  showLabel?: boolean;
  className?: string;
  interval?: ScoreInterval;
}

const defaultLimits = [25, 50, 75];

export const ScoreBar: React.FC<ScoreBarProps> = ({
  value,
  maxValue = 1,
  limits = defaultLimits,
  showLabel = true,
  className,
  interval,
}) => {
  const normalizedValue = Math.min(Math.max(value / maxValue, 0), 1);
  const percentage = normalizedValue * 100;
  const fillPercentage = normalizedValue > 0 ? Math.max(percentage, 2) : 0;
  const displayValue = value.toFixed(3);
  const barColor = MapperHelper.getRiskIndicatorColor(percentage, limits);

  const cssVars = interval
    ? ({
        '--lo-pct': `${interval.lo * 100}%`,
        '--hi-pct': `${(interval.hi - interval.lo) * 100}%`,
      } as React.CSSProperties)
    : undefined;

  return (
    <div
      className={clsx(styles.scoreBarContainer, interval && styles.hasInterval, className)}
      style={cssVars}
    >
      <div className={styles.barWrapper}>
        <div
          className={styles.barFill}
          style={{ width: `${fillPercentage}%`, backgroundColor: barColor }}
        />
        {interval && (
          <div
            className={styles.scoreDot}
            style={{ left: `${fillPercentage}%`, backgroundColor: barColor }}
          />
        )}
      </div>
      {showLabel && (
        <div className={styles.labelContainer}>
          <span
            className={styles.valueLabel}
            style={{ color: barColor }}
          >
            {displayValue}
          </span>
          <span className={styles.intervalLabel}>{interval?.label ?? '\u2014'}</span>
        </div>
      )}
    </div>
  );
};
